/**
 * The top of a screen: what it is, and what you can do here.
 *
 * Every screen was inventing its own header, and they disagreed about where the
 * title sat, how big it was, and where the primary action went. A user who
 * learns that the coloured button top-right is the action should not have to
 * re-learn it per screen.
 *
 * The primary action therefore has ONE home — the right of the header — and the
 * component makes that structural rather than a convention somebody has to
 * remember.
 */
import type { ReactNode } from 'react';
import { cn } from '../lib/cn';

export interface PageHeaderProps {
  readonly title: string;
  /** One line saying what the screen is for. Optional, never mandatory prose. */
  readonly description?: string;
  /** THE primary action. Rendered right, in the accent colour, alone. */
  readonly action?: ReactNode;
  /** Secondary controls, left of the primary action. */
  readonly actions?: ReactNode;
  /** Filters and search. Sits under the title row, full width. */
  readonly toolbar?: ReactNode;
  readonly className?: string;
}

export function PageHeader({
  title,
  description,
  action,
  actions,
  toolbar,
  className,
}: PageHeaderProps) {
  return (
    <header className={cn('mb-4 flex flex-col gap-3', className)}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          {/* `h1` on every screen: the page's own name is the document's top
              heading, and a screen reader navigating by heading needs it. */}
          <h1 className="text-lg font-semibold text-slate-100">{title}</h1>
          {description ? <p className="mt-0.5 text-xs text-slate-500">{description}</p> : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {actions}
          {action}
        </div>
      </div>
      {toolbar ? <div className="flex flex-wrap items-end gap-2">{toolbar}</div> : null}
    </header>
  );
}
